/**
 * Tage und Zeitpunkte, wie sie in diesem Haus geschrieben werden.
 *
 * DER SERVER SCHICKT ISO. `'2026-10-15'` ist das Datum, nicht seine
 * Darstellung: So gehört es in die Leitung — und so gehört es nicht auf den
 * Bildschirm. In einer Rechnungsliste liest niemand ein Fälligkeitsdatum in
 * dieser Form; er liest es falsch herum oder gar nicht.
 *
 * DIE ZONE STEHT FEST und kommt NICHT aus dem Browser: FRS-01 macht
 * Europe/Berlin zur maßgeblichen Zone. Ein Rechner mit anderer Einstellung
 * zeigte sonst ein anderes Datum als der Kollege am Telefon — ein Training um
 * 00:30 Berliner Zeit stünde in einem UTC-Browser am Vortag.
 *
 * WAS KEIN DATUM IST, GEHT UNVERÄNDERT DURCH, und aus `null` wird kein
 * Strich-Ersatz: Ein Gedankenstrich ist die Aussage „hier steht nichts", und
 * die trifft der Aufrufer, nicht diese Funktion.
 */
const ZONE = 'Europe/Berlin';

/** „15.10.2026" aus `2026-10-15` oder einem Zeitstempel. */
export function alsTag(wert: string | null | undefined): string | null {
  if (wert === null || wert === undefined || wert.trim() === '') return null;
  const zeit = new Date(wert.length === 10 ? `${wert}T12:00:00Z` : wert);
  if (Number.isNaN(zeit.getTime())) return wert;
  return zeit.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: ZONE,
  });
}

/**
 * „15.10.2026, 09:30 Uhr" aus einem Zeitstempel.
 *
 * Ein reines Tagesdatum bekommt KEINE erfundene Uhrzeit: Aus `2026-10-15`
 * würde sonst „00:00 Uhr", und das ist eine Angabe, die niemand gemacht hat.
 */
export function alsZeitpunkt(wert: string | null | undefined): string | null {
  if (wert === null || wert === undefined || wert.trim() === '') return null;
  if (wert.length === 10) return alsTag(wert);
  const zeit = new Date(wert);
  if (Number.isNaN(zeit.getTime())) return wert;
  const tag = alsTag(wert);
  const uhr = zeit.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: ZONE,
  });
  return `${tag}, ${uhr} Uhr`;
}

/**
 * Der heutige Geschäftstag als ISO-Datum, in der maßgeblichen Zone (FRS-01).
 *
 * Für Vorgaben, die der Server ohnehin nachprüft — das Rechnungsdatum setzt
 * er bei der Festschreibung selbst (N5.1, Migration 0256).
 */
export function heute(): string {
  // 'sv-SE' schreibt ein Datum als JJJJ-MM-TT.
  return new Intl.DateTimeFormat('sv-SE', { timeZone: ZONE }).format(new Date());
}
